import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { X, Sparkles, Check, ShieldCheck, Loader2, Play, Users, Gift, Copy, ArrowUp, Share2 } from 'lucide-react';
import { useReferral } from './useReferral';
// Pre-computed particle vectors for the success-state confetti burst, so the
// pieces fan out evenly in all directions from the checkmark.
const CONFETTI_COLORS = ['#EA580C', '#F59E0B', '#22C55E', '#2B211A', '#FB923C'];
const CONFETTI = Array.from({
  length: 14
}, (_, i) => {
  const angle = i / 14 * Math.PI * 2;
  const dist = 30 + i % 3 * 8;
  return {
    x: Math.cos(angle) * dist,
    y: Math.sin(angle) * dist,
    r: (i % 2 === 0 ? 1 : -1) * (90 + i % 4 * 30),
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length]
  };
});
type VideoKind = 'youtube' | 'vimeo' | 'loom' | 'file' | null;
// Detect how to embed whatever URL Tony drops in, so we render the right
// player without him having to think about it.
function getVideoEmbed(url?: string): {
  kind: VideoKind;
  src: string;
} | null {
  if (!url) return null;
  const u = url.trim();
  if (!u) return null;
  // YouTube (watch, youtu.be, or already-embed)
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return {
    kind: 'youtube',
    src: `https://www.youtube.com/embed/${yt[1]}`
  };
  // Loom
  const loom = u.match(/loom\.com\/(?:share|embed)\/([\w-]+)/);
  if (loom) return {
    kind: 'loom',
    src: `https://www.loom.com/embed/${loom[1]}`
  };
  // Vimeo
  const vimeo = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return {
    kind: 'vimeo',
    src: `https://player.vimeo.com/video/${vimeo[1]}`
  };
  // Direct video file
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(u)) return {
    kind: 'file',
    src: u
  };
  // Fallback: assume it's already an embeddable URL
  return {
    kind: 'youtube',
    src: u
  };
}
export function WaitlistModal({
  onClose,
  videoUrl,
  // The code of whoever referred this visitor (read from ?ref= / the capture
  // route). Drives the "you were invited" banner and, when wired to a backend,
  // the signup attribution.
  referredBy
}: {
  onClose: () => void;
  videoUrl?: string;
  referredBy?: string | null;
}) {
  const reduceMotion = useReducedMotion();
  const [email, setEmail] = useState('');
  const [keepPosted, setKeepPosted] = useState(true);
  const [status, setStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [copied, setCopied] = useState(false);
  const embed = getVideoEmbed(videoUrl);
  const isDone = status === 'done';
  // The visitor's own shareable invite link + who (if anyone) referred them.
  // ⚠️ BACKEND TODO: replace the generated code with the one returned by
  // POST /waitlist when the real API exists (see components/useReferral.ts).
  const {
    referralLink,
    referredBy: storedReferrer
  } = useReferral();
  const inviter = (referredBy ?? storedReferrer)?.trim() || null;
  // Illustrative queue position shown on the success state. Picked once so it
  // stays stable while the success card is visible. Wire this to the real
  // position returned by the backend when the waitlist API is live.
  const queuePosition = useMemo(() => 120 + Math.floor(Math.random() * 80), []);
  // Pretty link (no protocol) for display; full link is used for copy/share.
  const displayLink = referralLink.replace(/^https?:\/\//, '');
  const shareText = 'I just joined the Giga waitlist — grab your spot:';
  const handleShare = async () => {
    // Prefer the native share sheet on supported devices; fall back to copy.
    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({
          title: 'Giga',
          text: shareText,
          url: referralLink
        });
        return;
      } catch {
        /* user dismissed — fall through to copy */
      }
    }
    handleCopy();
  };
  const handleCopy = () => {
    const write = navigator?.clipboard?.writeText?.(referralLink);
    Promise.resolve(write).finally(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== 'idle') return;
    setStatus('loading');
    // ⚠️ BACKEND TODO: POST /waitlist { email, referredBy: inviter, keepPosted }
    // and use the returned { code, position } instead of the local fallbacks.
    window.setTimeout(() => setStatus('done'), 700);
  };
  return <motion.div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.12,
    ease: 'easeOut'
  }} data-id="element-3060">
      {/* Backdrop — deeper, with a warm radial vignette so the card reads as
                    floating in a softly lit room rather than over a flat scrim. */}
      <div className="absolute inset-0 bg-[#140F0B]/85 backdrop-blur-md" onClick={onClose} aria-hidden="true" data-id="element-3061" />
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
      background: 'radial-gradient(120% 90% at 50% 38%, rgba(234,88,12,0.10) 0%, rgba(20,15,11,0) 55%)'
    }} data-id="element-3062" />

      <motion.div role="dialog" aria-modal="true" aria-label="Join the Giga waitlist" className="group/modal relative w-full max-w-2xl max-h-[90dvh] overflow-auto overscroll-contain rounded-[28px] bg-[#FBF9F5] text-[#2B211A] ring-1 ring-black/5 shadow-[0_2px_8px_rgba(20,15,11,0.06),0_40px_80px_-24px_rgba(20,15,11,0.55)]" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.98,
      y: 8
    }} transition={reduceMotion ? {
      duration: 0.12
    } : {
      type: 'spring',
      stiffness: 420,
      damping: 32,
      mass: 0.7
    }} data-id="element-3063">
        {/* Premium surface detail: a bright hairline along the top edge and a
            whisper of film grain across the whole card, so it reads like a
            crafted physical surface instead of a flat panel. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent" data-id="element-3064" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[28px] opacity-[0.025] mix-blend-multiply" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
      }} data-id="element-3065" />

        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/[0.04] ring-1 ring-black/5 hover:bg-black/[0.08] text-[#6B5D52] hover:text-[#2B211A] hover:rotate-90 transition-all duration-300" data-id="element-3066">
          <X size={18} data-id="element-3067" />
        </button>

        <div className="relative p-5 sm:p-7 md:p-9" data-id="element-3068">
          <AnimatePresence mode="wait" data-id="element-3069">
            {isDone ?
          // ── SUCCESS VIEW ──────────────────────────────────────────────
          // A focused, self-contained celebration that REPLACES the form +
          // video so the content always fits the card (no clipping). This
          // is the referral hub: queue position + share-to-skip-the-line.
          <motion.div key="success" initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.25
          }} className="flex flex-col items-center text-center" data-id="element-3070">
                {/* Celebration mark with confetti burst */}
                <span className="relative flex items-center justify-center w-16 h-16 mb-5" data-id="element-3071">
                  {!reduceMotion && <span className="pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-3072">
                      {CONFETTI.map((c, i) => <motion.span key={i} className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-[1px]" style={{
                  backgroundColor: c.color
                }} initial={{
                  opacity: 0,
                  scale: 0,
                  x: 0,
                  y: 0
                }} animate={{
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0.6],
                  x: c.x,
                  y: c.y,
                  rotate: c.r
                }} transition={{
                  duration: 0.9,
                  delay: 0.12,
                  ease: [0.16, 1, 0.3, 1]
                }} data-id="element-3073" />)}
                    </span>}
                  <motion.span className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-b from-[#F97316] to-[#EA580C] ring-1 ring-white/20 shadow-[0_8px_24px_rgba(234,88,12,0.5)]" initial={reduceMotion ? {
                opacity: 0
              } : {
                scale: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={reduceMotion ? {
                duration: 0.2
              } : {
                type: 'spring',
                stiffness: 500,
                damping: 18,
                delay: 0.05
              }} data-id="element-3074">
                    <Check size={28} className="text-white" strokeWidth={3} data-id="element-3075" />
                  </motion.span>
                </span>

                <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2" data-id="element-3076">
                  You're on the list.
                </h2>
                <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-md mb-6" data-id="element-3077">
                  We email teams in the order they joined — we'll be in touch
                  the moment your spot opens up.
                </p>

                {/* Queue position chip */}
                <div className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#EFE9E1] px-4 py-2 mb-7 shadow-[0_1px_2px_rgba(20,15,11,0.05)]" data-id="element-3078">
                  <Users size={15} className="text-[#EA580C]" data-id="element-3079" />
                  <span className="text-sm font-semibold text-[#2B211A]" data-id="element-3080">
                    You're{' '}
                    <span className="text-[#EA580C]" data-id="element-3081">
                      #{queuePosition.toLocaleString()}
                    </span>{' '}
                    in line
                  </span>
                </div>

                {/* Skip-the-line referral hub */}
                <div className="w-full rounded-2xl bg-white ring-1 ring-[#EFE9E1] shadow-[0_10px_30px_-12px_rgba(20,15,11,0.18)] p-5 text-left" data-id="element-3082">
                  <div className="flex items-start gap-3 mb-4" data-id="element-3083">
                    <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-[#EA580C]/[0.08] ring-1 ring-[#EA580C]/15 shrink-0" data-id="element-3084">
                      <Gift size={17} className="text-[#EA580C]" data-id="element-3085" />
                    </span>
                    <div className="min-w-0" data-id="element-3086">
                      <p className="font-bold text-[#2B211A] leading-tight" data-id="element-3087">
                        Want in sooner? Skip the line.
                      </p>
                      <p className="text-sm text-[#6B5D52] leading-relaxed mt-0.5" data-id="element-3088">
                        Share your invite — just 3 teams through your link jumps
                        you near the front.
                      </p>
                    </div>
                  </div>

                  {/* Invite link + copy / share */}
                  <div className="flex flex-col sm:flex-row items-stretch gap-2" data-id="element-3089">
                    <div className="flex-1 flex items-center gap-2 rounded-xl bg-[#FBF9F5] ring-1 ring-[#EFE9E1] px-3 py-2.5 min-w-0" data-id="element-3090">
                      <span className="text-sm text-[#2B211A] truncate font-medium" data-id="element-3091">
                        {displayLink}
                      </span>
                    </div>
                    <div className="flex items-stretch gap-2" data-id="element-3092">
                      <button type="button" onClick={handleCopy} className="flex items-center justify-center gap-1.5 rounded-xl bg-[#2B211A] px-4 py-2.5 text-sm font-semibold text-white whitespace-nowrap transition-colors hover:bg-[#3a2c22]" data-id="element-3093">
                        {copied ? <>
                            <Check size={15} strokeWidth={2.5} data-id="element-3094" />
                            Copied
                          </> : <>
                            <Copy size={15} data-id="element-3095" />
                            Copy
                          </>}
                      </button>
                      <button type="button" onClick={handleShare} aria-label="Share your invite link" className="flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-b from-[#F97316] to-[#EA580C] px-4 py-2.5 text-sm font-semibold text-white whitespace-nowrap ring-1 ring-[#C2410C]/40 shadow-[0_6px_16px_-6px_rgba(234,88,12,0.6)] transition-all hover:-translate-y-px" data-id="element-3096">
                        <Share2 size={15} data-id="element-3097" />
                        Share
                      </button>
                    </div>
                  </div>

                  <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#16A34A]" data-id="element-3098">
                    <ArrowUp size={12} strokeWidth={2.5} data-id="element-3099" />
                    Each referral jumps you 25 spots
                  </p>
                </div>
              </motion.div> :
          // ── FORM VIEW ─────────────────────────────────────────────────
          <motion.div key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-3100">
                {/* Eyebrow — set in a soft tinted pill so the label feels like a
                 badge rather than loose text. */}
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#EA580C]/[0.08] ring-1 ring-[#EA580C]/15 px-3 py-1 text-[11px] font-semibold uppercase text-[#C2410C] mb-4 [letter-spacing:4px]" data-id="element-3101">
                  <Sparkles size={12} data-id="element-3102" />
                  Early access
                </div>

                {/* "You were invited" banner — shown when the visitor arrives
                 via a referral link, so the referral loop is visible on the
                 receiving end too. */}
                {inviter && <div className="flex items-center gap-2 rounded-xl bg-white ring-1 ring-[#EFE9E1] px-3.5 py-2.5 mb-4 shadow-[0_1px_2px_rgba(20,15,11,0.05)]" data-id="element-3103">
                    <Gift size={15} className="text-[#EA580C] shrink-0" data-id="element-3104" />
                    <p className="text-sm text-[#2B211A] font-medium" data-id="element-3105">
                      You've been invited to skip ahead — join below to claim
                      your spot.
                    </p>
                  </div>}

                {/* Heading — extra right padding on mobile so the long title
                 never tucks under the close button. */}
                <h2 className="text-2xl md:text-3xl font-title font-bold tracking-tight leading-tight mb-2 pr-8 sm:pr-0" data-id="element-3106">
                  Giga is invite-only — for now.
                </h2>
                <p className="text-sm md:text-base text-[#6B5D52] leading-relaxed max-w-xl mb-4" data-id="element-3107">
                  We're letting teams in a few at a time. Watch the quick
                  walkthrough, then grab your spot on the waitlist — we'll email
                  you the moment you're in.
                </p>

                {/* Truthful scarcity cue — no fabricated counts, just the honest
                 cadence: we onboard a limited number of teams each week. */}
                <div className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#EFE9E1] px-3.5 py-1.5 text-xs font-semibold text-[#6B5D52] mb-5 md:mb-6 shadow-[0_1px_2px_rgba(20,15,11,0.05)]" data-id="element-3108">
                  <Users size={13} className="text-[#EA580C]" data-id="element-3109" />
                  Onboarding a limited number of teams each week
                </div>

                {/* Tony: drop the product walkthrough here — pass videoUrl
                 (YouTube/Loom/Vimeo/.mp4) to <WaitlistModal videoUrl="..." /> */}
                <div className="aspect-video w-full rounded-2xl overflow-hidden ring-1 ring-black/10 shadow-[0_18px_40px_-18px_rgba(20,15,11,0.55)] bg-[#241814] mb-6 md:mb-7" data-id="element-3110">
                  {embed ? embed.kind === 'file' ? <video src={embed.src} controls playsInline className="w-full h-full object-cover" data-id="element-3111" /> : <iframe src={embed.src} title="Giga product walkthrough" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-3112" /> : <div className="relative w-full h-full flex flex-col items-center justify-center gap-4" data-id="element-3113">
                      {/* Deep, dimensional base + faint dotted texture + a top
                       gloss sweep so the well reads as a polished screen. */}
                      <div className="absolute inset-0" style={{
                  background: 'radial-gradient(110% 80% at 50% 40%, #2E1E16 0%, #211611 60%, #160E0A 100%)'
                }} data-id="element-3114" />
                      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-3115" />
                      <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-white/[0.06] to-transparent" data-id="element-3116" />
                      <div className="relative flex items-center justify-center w-16 h-16" data-id="element-3117">
                        {!reduceMotion && <motion.span aria-hidden="true" className="absolute inset-0 rounded-full ring-2 ring-[#EA580C]/40" initial={{
                    scale: 1,
                    opacity: 0.5
                  }} animate={{
                    scale: [1, 1.6],
                    opacity: [0.5, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} data-id="element-3118" />}
                        <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-b from-[#F97316] to-[#EA580C] ring-1 ring-white/20 shadow-[0_8px_24px_rgba(234,88,12,0.5)]" data-id="element-3119">
                          <Play size={26} className="text-white translate-x-0.5" fill="currentColor" data-id="element-3120" />
                        </span>
                      </div>
                      <p className="relative text-sm font-medium text-white/70" data-id="element-3121">
                        Tony's walkthrough lands here
                      </p>
                    </div>}
                </div>

                {/* Waitlist form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-3122">
                  <div className="flex flex-col sm:flex-row items-stretch gap-2.5" data-id="element-3123">
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className="flex-1 px-4 py-3 rounded-xl bg-white ring-1 ring-[#E7E0D7] border-0 text-[#2B211A] placeholder:text-[#9C8E82] shadow-[inset_0_1px_2px_rgba(20,15,11,0.04)] focus:outline-none focus:ring-2 focus:ring-[#EA580C]/45 transition-shadow" data-id="element-3124" />
                    <button type="submit" disabled={status === 'loading'} className="relative overflow-hidden flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-b from-[#F97316] to-[#EA580C] text-white font-bold whitespace-nowrap shadow-[0_8px_20px_-6px_rgba(234,88,12,0.6)] ring-1 ring-[#C2410C]/40 transition-all duration-200 hover:shadow-[0_10px_26px_-6px_rgba(234,88,12,0.7)] hover:-translate-y-px active:translate-y-0 disabled:opacity-80 disabled:hover:translate-y-0 [&>span.sheen]:hover:translate-x-[180%]" data-id="element-3125">
                      <span aria-hidden="true" className="sheen pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 -skew-x-12 bg-white/25 blur-md -translate-x-[120%] transition-transform duration-700 ease-out" data-id="element-3126" />
                      {status === 'loading' ? <>
                          <Loader2 size={16} className="animate-spin" data-id="element-3127" />
                          Joining…
                        </> : 'Join the waitlist'}
                    </button>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-[#6B5D52] cursor-pointer select-none" data-id="element-3128">
                    <input type="checkbox" checked={keepPosted} onChange={e => setKeepPosted(e.target.checked)} className="w-4 h-4 rounded border-[#D8CEC2] text-[#EA580C] focus:ring-[#EA580C]/30 accent-[#EA580C]" data-id="element-3129" />
                    Keep me posted on product news
                  </label>

                  <p className="flex items-center gap-1.5 text-xs text-[#9C8E82]" data-id="element-3130">
                    <ShieldCheck size={12} data-id="element-3131" />
                    No spam. We'll only email about your spot.
                  </p>
                </form>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>;
}